/**
 * Menu items configuration for CoOrdio Cortex navigation.
 * CoOrdio Design System icons (Lucide) and routes.
 */

// React Imports
import type { ComponentType } from 'react';

// CoOrdio DS Icons (Lucide)
import { Icons } from '@core/components/icons';

// Utils
import { ROUTES } from '@/utils/routes';

/**
 * Menu item type
 */
export interface MenuItem {
  title: string;
  icon?: ComponentType;
  path?: string;
  children?: MenuItem[];
  badge?: string | number;
  badgeColor?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
  externalLink?: boolean;
  /** When true, this item is only enabled when a tenant is selected in the context store. */
  requiresTenant?: boolean;
  /** When true, this item is visible only to users with global access. */
  requiresGlobalAccess?: boolean;
}

/** Module key for sections that can be turned on/off via app settings. */
export type ModuleKey = 'Automation' | 'Governance' | 'Integrations';

/**
 * A labelled group of menu items rendered in the sidebar.
 */
export interface MenuSection {
  label?: string;
  /** When set, this section is only shown when the module is enabled in app config. */
  moduleKey?: ModuleKey;
  items: MenuItem[];
}

/**
 * Menu sections — each section has an optional header label and a list of items.
 */
export const menuSections: MenuSection[] = [
  {
    // Top-level overview items — no label (always visible)
    items: [
      { title: 'Dashboard', icon: Icons.LayoutDashboard, path: ROUTES.HOME },
      { title: 'Customize', icon: Icons.SlidersHorizontal, path: ROUTES.DASHBOARD_CUSTOMIZE },
      { title: 'Analytics', icon: Icons.BarChart3, path: ROUTES.USAGE_ANALYTICS },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { title: 'Projects', icon: Icons.Folder, path: ROUTES.PROJECTS, requiresTenant: true },
      { title: 'API Keys', icon: Icons.Key, path: ROUTES.API_KEYS, requiresTenant: true },
    ],
  },
  {
    label: 'AI Operations',
    items: [
      { title: 'Model Catalog', icon: Icons.Brain, path: ROUTES.MODEL_CATALOG },
      { title: 'Agents', icon: Icons.Bot, path: ROUTES.AGENTS, requiresTenant: true },
      { title: 'Sessions', icon: Icons.MessageSquare, path: ROUTES.SESSIONS, requiresTenant: true },
      { title: 'Memories', icon: Icons.Brain, path: ROUTES.MEMORIES, requiresTenant: true },
      { title: 'Skills', icon: Icons.ClipboardList, path: ROUTES.SKILLS, requiresTenant: true },
      { title: 'Tools', icon: Icons.Wrench, path: ROUTES.TOOLS, requiresTenant: true },
      // { title: 'Orchestrator', icon: Icons.GitBranch, path: ROUTES.ORCHESTRATOR, requiresTenant: true },
      { title: 'Agent Functions', icon: Icons.Code, path: ROUTES.AGENT_FUNCTIONS, requiresTenant: true },
      { title: 'API Explorer', icon: Icons.Terminal, path: ROUTES.API_EXPLORER, requiresTenant: true },
    ],
  },
  {
    label: 'Automation',
    moduleKey: 'Automation',
    items: [
      { title: 'Workflows', icon: Icons.GitBranch, path: ROUTES.WORKFLOWS, requiresTenant: true },
      { title: 'Schedules', icon: Icons.Calendar, path: ROUTES.SCHEDULES, requiresTenant: true },
      { title: 'Investigations', icon: Icons.Bug, path: ROUTES.INVESTIGATIONS, requiresTenant: true },
      { title: 'Templates', icon: Icons.Bug, path: ROUTES.INVESTIGATION_TEMPLATES, requiresTenant: true },
    ],
  },
  {
    label: 'Governance',
    moduleKey: 'Governance',
    items: [
      { title: 'Safety Gates', icon: Icons.Shield, path: ROUTES.SAFETY_GATES, requiresTenant: true },
      { title: 'Approvals', icon: Icons.CheckCircle, path: ROUTES.APPROVAL_QUEUE, requiresTenant: true },
    ],
  },
  {
    label: 'Integrations',
    moduleKey: 'Integrations',
    items: [
      { title: 'MCP Servers', icon: Icons.Cloud, path: ROUTES.MCP_SERVERS, requiresTenant: true },
      { title: 'External Systems', icon: Icons.Webhook, path: ROUTES.EXTERNAL_SYSTEMS, requiresTenant: true },
      { title: 'Connectors', icon: Icons.Link, path: ROUTES.CONNECTORS, requiresTenant: true },
    ],
  },
  {
    label: 'Settings',
    items: [
      { title: 'LLM Providers', icon: Icons.Key, path: ROUTES.LLM_PROVIDER_CONFIGS, requiresTenant: true, requiresGlobalAccess: true },
      { title: 'User Settings', icon: Icons.Settings, path: ROUTES.SETTINGS_USER },
      { title: 'Tenant Settings', icon: Icons.Building2, path: ROUTES.SETTINGS_TENANT, requiresTenant: true },
      { title: 'Project Settings', icon: Icons.Folder, path: ROUTES.SETTINGS_PROJECT, requiresTenant: true },
    ],
  },
];

/**
 * Flat list of all menu items (for backwards compat with any code that imports menuItems).
 */
export const menuItems: MenuItem[] = menuSections.flatMap((s) => s.items);
